import React from 'react';
import { Shield, FileText, Bell, Eye, Lock, RefreshCw } from 'lucide-react';
import { services } from '../data/mock';

const ServicesSection = ({ onOpenForm }) => {
  const serviceIcons = {
    'Comprehensive Trademark Search': <Eye size={32} />,
    'Expert USPTO Filing Services': <FileText size={32} />,
    'Office Action Response Strategy': <Shield size={32} />,
    'Trademark Monitoring & Enforcement': <Bell size={32} />,
    'Brand Protection Strategies': <Lock size={32} />,
    'Trademark Renewal & Maintenance': <RefreshCw size={32} />
  };

  return (
    <>
      {/* Trademark Overview Section */}
      <section id="overview" className="py-20 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-secondary font-semibold text-sm uppercase tracking-wide">What is Trademark Registration</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
                Understanding Trademark Protection
              </h2>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                A trademark is a unique identifier—such as a word, phrase, symbol, design, or combination thereof—that distinguishes your goods or services from those of others. Trademark registration with the United States Patent and Trademark Office (USPTO) provides federal legal protection for your brand across all 50 states.
              </p>
              
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                When you register a trademark, you gain exclusive rights to use that mark in connection with your specific goods or services. This prevents competitors from using confusingly similar marks that could mislead customers or dilute your brand identity. Federal trademark registration is essential for businesses that want to protect their market position, build brand equity, and prevent infringement.
              </p>

              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8">
                The registration process involves comprehensive searching of existing marks, preparing and filing a detailed application with proper classification of goods and services, responding to any USPTO examiner concerns, publication for opposition, and finally receiving your registration certificate. This process typically takes 8-12 months and results in powerful legal rights that can last indefinitely with proper maintenance. Your registered trademark becomes a valuable business asset that can be licensed, sold, or used as collateral for financing.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-accent/50 p-6 rounded-lg text-center">
                  <div className="text-secondary text-2xl font-bold mb-2">Nationwide</div>
                  <div className="text-sm text-gray-600">Protection across all 50 states and U.S. territories</div>
                </div>
                <div className="bg-accent/50 p-6 rounded-lg text-center">
                  <div className="text-secondary text-2xl font-bold mb-2">Exclusive</div>
                  <div className="text-sm text-gray-600">Rights to use your mark in your industry</div>
                </div>
                <div className="bg-accent/50 p-6 rounded-lg text-center">
                  <div className="text-secondary text-2xl font-bold mb-2">Legal</div>
                  <div className="text-sm text-gray-600">Federal court enforcement with statutory damages</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Our Services</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
                Comprehensive Trademark Registration Services
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
                We offer end-to-end trademark services to protect your brand at every stage, from initial search to ongoing monitoring and renewal.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-accent rounded-lg p-8 hover:border-secondary hover:shadow-xl transition-all group flex flex-col h-full"
                >
                  <div className="bg-accent w-16 h-16 rounded-lg flex items-center justify-center mb-4 group-hover:bg-secondary transition-all flex-shrink-0">
                    <div className="text-secondary group-hover:text-white transition-colors">
                      {serviceIcons[service.title]}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-4 flex-shrink-0">{service.title}</h3>
                  <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: '300px' }}>
                    <p className="text-gray-600 leading-relaxed text-sm">{service.description}</p>
                  </div>
                  <button
                    onClick={onOpenForm}
                    className="text-secondary font-semibold text-sm hover:underline inline-flex items-center flex-shrink-0"
                  >
                    Get Started →
                  </button>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="mt-16 text-center bg-primary text-white rounded-lg p-12">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Ready to Protect Your Brand?
              </h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Don't wait until someone else registers your brand name. Start your trademark registration process today with our expert team.
              </p>
              <button
                onClick={onOpenForm}
                className="bg-secondary text-white px-8 py-4 rounded-md hover:bg-secondary/90 transition-all font-semibold shadow-lg text-lg"
              >
                Get Started Now
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
